import { useEffect, useState } from "react";
import { getSurveyByToken, submitSurvey } from "./data.js";

function StarRating({ value, onChange, disabled }) {
  const [hovered, setHovered] = useState(0);
  return (
    <div style={{ display: "flex", gap: 8, justifyContent: "center", margin: "20px 0" }}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          disabled={disabled}
          onMouseEnter={() => !disabled && setHovered(n)}
          onMouseLeave={() => !disabled && setHovered(0)}
          onClick={() => !disabled && onChange(n)}
          style={{
            background: "none",
            border: "none",
            fontSize: 44,
            cursor: disabled ? "default" : "pointer",
            color: n <= (hovered || value) ? "#f59e0b" : "#d1d5db",
            transform: n <= (hovered || value) ? "scale(1.15)" : "scale(1)",
            transition: "all 0.15s",
            padding: "4px",
          }}
          aria-label={`${n} bintang`}
        >
          ★
        </button>
      ))}
    </div>
  );
}

const RATING_LABELS = ["", "Sangat Buruk", "Buruk", "Cukup", "Baik", "Sangat Baik"];

function Countdown({ expiresAt }) {
  const [remaining, setRemaining] = useState("");

  useEffect(() => {
    function tick() {
      const diff = new Date(expiresAt) - new Date();
      if (diff <= 0) { setRemaining("EXPIRED"); return; }
      const m = Math.floor(diff / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setRemaining(`${m}:${String(s).padStart(2, "0")}`);
    }
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [expiresAt]);

  if (remaining === "EXPIRED") return (
    <div style={{ background: "#fee2e2", color: "#dc2626", borderRadius: 8, padding: "8px 16px", fontSize: 13, fontWeight: 600, textAlign: "center" }}>
      ⏰ Waktu habis — voucher tidak tersedia
    </div>
  );

  const mins = parseInt(remaining.split(":")[0], 10);
  const color = mins < 10 ? "#dc2626" : "#d97706";
  return (
    <div style={{ background: "#fffbeb", border: `1px solid ${color}40`, borderRadius: 8, padding: "8px 16px", fontSize: 13, fontWeight: 600, textAlign: "center", color }}>
      ⏳ Isi dalam <strong>{remaining}</strong> untuk dapatkan voucher spesial!
    </div>
  );
}

export function SurveyPage({ token }) {
  const [survey, setSurvey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // { expired, voucher_code }

  useEffect(() => {
    (async () => {
      try {
        const data = await getSurveyByToken(token);
        setSurvey(data);
        if (data.submitted_at) {
          setResult({ expired: !data.voucher_code, voucher_code: data.voucher_code });
        }
      } catch {
        setError("Link survey tidak valid atau sudah kadaluwarsa.");
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  async function handleSubmit() {
    if (!rating) { setError("Pilih rating bintang dulu."); return; }
    setSubmitting(true);
    setError("");
    try {
      const res = await submitSurvey(token, { rating, comment });
      setResult(res);
    } catch (e) {
      setError(e.message || "Gagal mengirim survey.");
    } finally {
      setSubmitting(false);
    }
  }

  const isExpired = survey && new Date(survey.expires_at) < new Date() && !survey.submitted_at;

  return (
    <div style={{
      minHeight: "100vh",
      background: "linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 50%, #f0f9ff 100%)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: 20,
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    }}>
      <div style={{
        background: "#fff",
        borderRadius: 20,
        boxShadow: "0 8px 40px rgba(0,0,0,0.10)",
        padding: "36px 32px",
        maxWidth: 420,
        width: "100%",
      }}>
        {/* Header */}
        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <div style={{ fontSize: 40, marginBottom: 8 }}>🌿</div>
          <h1 style={{ fontSize: 20, fontWeight: 800, color: "#111827", margin: "0 0 4px" }}>
            Survei Kepuasan
          </h1>
          <p style={{ fontSize: 13, color: "#6b7280", margin: 0 }}>
            Halo{survey?.client_name ? `, ${survey.client_name}` : ""}! Bagaimana pelayanan kami hari ini?
          </p>
        </div>

        {loading && (
          <div style={{ textAlign: "center", color: "#9ca3af", padding: 20 }}>Memuat…</div>
        )}

        {!loading && error && !result && (
          <div style={{ background: "#fee2e2", color: "#dc2626", borderRadius: 10, padding: "12px 16px", fontSize: 14, textAlign: "center" }}>
            {error}
          </div>
        )}

        {!loading && !error && survey && !result && !isExpired && (
          <>
            <Countdown expiresAt={survey.expires_at} />
            <StarRating value={rating} onChange={setRating} />
            {rating > 0 && (
              <div style={{ textAlign: "center", fontSize: 14, fontWeight: 600, color: "#059669", marginBottom: 12 }}>
                {RATING_LABELS[rating]}
              </div>
            )}
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Ceritakan pengalaman kamu (opsional)…"
              rows={3}
              style={{
                width: "100%",
                padding: "10px 14px",
                border: "1px solid #e5e7eb",
                borderRadius: 10,
                fontSize: 14,
                resize: "vertical",
                boxSizing: "border-box",
                fontFamily: "inherit",
              }}
            />
            {error && (
              <div style={{ color: "#dc2626", fontSize: 13, marginTop: 6 }}>{error}</div>
            )}
            <button
              onClick={handleSubmit}
              disabled={submitting}
              style={{
                width: "100%",
                marginTop: 16,
                padding: "13px",
                background: "#059669",
                color: "#fff",
                border: "none",
                borderRadius: 12,
                fontSize: 15,
                fontWeight: 700,
                cursor: submitting ? "not-allowed" : "pointer",
                opacity: submitting ? 0.7 : 1,
              }}
            >
              {submitting ? "Mengirim…" : "Kirim Survey ✓"}
            </button>
          </>
        )}

        {!loading && !error && isExpired && (
          <div style={{ textAlign: "center", padding: "20px 0" }}>
            <div style={{ fontSize: 36, marginBottom: 8 }}>⏰</div>
            <p style={{ color: "#dc2626", fontWeight: 600 }}>Link survey sudah kadaluwarsa.</p>
            <p style={{ color: "#6b7280", fontSize: 13 }}>Survey hanya bisa diisi selama link masih aktif.</p>
          </div>
        )}

        {result && (
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>
              {result.voucher_code ? "🎉" : "✅"}
            </div>
            <h2 style={{ fontSize: 18, fontWeight: 800, color: "#111827", margin: "0 0 8px" }}>
              Terima kasih!
            </h2>
            <p style={{ color: "#6b7280", fontSize: 14, margin: "0 0 16px" }}>
              Feedback kamu sangat berarti untuk kami.
            </p>
            {result.voucher_code ? (
              <div style={{
                background: "linear-gradient(135deg, #7c3aed, #4f46e5)",
                borderRadius: 14, padding: "20px 24px", color: "#fff", marginTop: 8,
              }}>
                <div style={{ fontSize: 32, marginBottom: 8 }}>🎁</div>
                <div style={{ fontSize: 13, fontWeight: 700, opacity: 0.85, marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  Hadiah Kejutan Menunggumu!
                </div>
                <div style={{ fontSize: 14, opacity: 0.85, marginBottom: 16 }}>
                  Tunjukkan kode ini ke kasir saat kunjungan berikutnya untuk mengetahui hadiahmu ✨
                </div>
                <div style={{ borderTop: "1px solid rgba(255,255,255,0.2)", paddingTop: 14 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, opacity: 0.8, marginBottom: 6, textTransform: "uppercase" }}>
                    Kode Voucher
                  </div>
                  <div style={{ fontSize: 26, fontWeight: 900, letterSpacing: 2, marginBottom: 6 }}>
                    {result.voucher_code}
                  </div>
                  <div style={{ fontSize: 12, opacity: 0.75 }}>
                    📸 Screenshot & simpan kode ini
                  </div>
                </div>
              </div>
            ) : (
              <p style={{ color: "#9ca3af", fontSize: 13 }}>
                Voucher tidak tersedia karena waktu sudah habis.
              </p>
            )}
          </div>
        )}

        <div style={{ textAlign: "center", marginTop: 24, fontSize: 11, color: "#d1d5db" }}>
          Powered by JarumDewa
        </div>
      </div>
    </div>
  );
}
